<template>
    <div class="mb-6 rounded-md border border-gray-200 bg-white p-4">
        <div class="mb-4 flex items-start justify-between gap-4">
            <div>
                <div class="text-sm font-medium text-gray-900">
                    Countries
                </div>

                <div class="mt-1 text-sm text-gray-500">
                    Choose which countries this Lite Integration applies to.
                </div>
            </div>

            <label class="flex shrink-0 cursor-pointer items-center gap-2">
                <input
                    v-model="countriesIncluded"
                    type="checkbox"
                    class="h-4 w-4"
                    :disabled="savingMode"
                    @change="updateCountriesIncluded"
                >

                <span class="text-sm font-medium">
                    {{ countriesIncluded ? 'Included' : 'Excluded' }}
                </span>
            </label>
        </div>

        <div class="mb-4 rounded-md bg-gray-50 p-3 text-sm text-gray-600">
            <template v-if="countriesIncluded">
                Only the selected countries are included in this integration.
            </template>

            <template v-else>
                The selected countries are excluded from this integration.
            </template>
        </div>

        <div
            v-if="error"
            class="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
            {{ error }}
        </div>

        <div
            v-if="selectedCountries.length"
            class="mb-5"
        >
            <div class="mb-2 text-sm font-medium text-gray-700">
                Selected Countries
            </div>

            <div class="flex flex-wrap gap-2">
                <div
                    v-for="country in selectedCountries"
                    :key="country.uuid"
                    class="flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-sm text-blue-700"
                >
                    <span>
                        {{ country.name }} ({{ country.code }})
                    </span>

                    <button
                        type="button"
                        class="font-medium text-blue-400 hover:text-red-600 disabled:opacity-50"
                        :disabled="savingCountryUuid === country.uuid"
                        @click="removeCountry(country)"
                    >
                        &times;
                    </button>
                </div>
            </div>
        </div>

        <div
            v-else-if="loadingSelected"
            class="mb-5 py-3 text-sm text-gray-500"
        >
            Loading selected countries...
        </div>

        <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
                Add Country
            </label>

            <input
                v-model.trim="search"
                type="text"
                class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="Search by country name or code..."
                @input="searchCountries"
            >
        </div>

        <div
            v-if="searching"
            class="py-5 text-center text-sm text-gray-500"
        >
            Searching...
        </div>

        <div
            v-else-if="search && !availableCountries.length"
            class="py-5 text-center text-sm text-gray-500"
        >
            No countries found.
        </div>

        <div
            v-else-if="availableCountries.length"
            class="mt-2 max-h-64 overflow-y-auto rounded-md border border-gray-200"
        >
            <button
                v-for="country in availableCountries"
                :key="country.uuid"
                type="button"
                class="flex w-full items-center justify-between gap-4 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="savingCountryUuid === country.uuid"
                @click="addCountry(country)"
            >
                <div>
                    <div class="text-sm font-medium text-gray-900">
                        {{ country.name }}
                    </div>

                    <div class="text-xs text-gray-500">
                        {{ country.code }}

                        <template v-if="country.code_alpha3">
                            · {{ country.code_alpha3 }}
                        </template>
                    </div>
                </div>

                <span class="text-sm text-blue-600">
                    {{
                        savingCountryUuid === country.uuid
                            ? 'Adding...'
                            : 'Add'
                    }}
                </span>
            </button>
        </div>

        <div class="mt-4 text-xs text-gray-500">
            {{ selectedCountries.length }}
            {{ selectedCountries.length === 1 ? 'country' : 'countries' }}
            selected.
        </div>
    </div>
</template>

<script>
export default {
    name: 'LiteIntegrationCountries',

    props: {
        liteIntegration: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            countries: [],
            selectedCountries: [],

            countriesIncluded:
                this.liteIntegration.countries_included === true ||
                Number(this.liteIntegration.countries_included) === 1,

            search: '',
            searching: false,
            loadingSelected: false,
            savingMode: false,
            savingCountryUuid: null,
            searchTimeout: null,
            searchRequestId: 0,
            error: null,
        };
    },

    computed: {
        selectedCountryUuids() {
            return this.selectedCountries.map(({ uuid }) => uuid);
        },

        availableCountries() {
            return this.countries.filter(
                ({ uuid }) => !this.selectedCountryUuids.includes(uuid)
            );
        },
    },

    mounted() {
        this.loadSelectedCountries();
    },

    beforeUnmount() {
        clearTimeout(this.searchTimeout);
    },

    methods: {
        async loadSelectedCountries() {
            this.loadingSelected = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    `/lite-integrations/${this.liteIntegration.uuid}/countries`
                );

                this.selectedCountries =
                    response.data.data ??
                    response.data ??
                    [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load selected countries.'
                );
            } finally {
                this.loadingSelected = false;
            }
        },

        searchCountries() {
            clearTimeout(this.searchTimeout);

            if (!this.search) {
                this.countries = [];
                this.searching = false;

                return;
            }

            this.searchTimeout = setTimeout(
                () => this.loadCountries(),
                300
            );
        },

        async loadCountries() {
            const search = this.search;

            if (!search) {
                this.countries = [];

                return;
            }

            const requestId = ++this.searchRequestId;

            this.searching = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    '/singlestore-countries',
                    {
                        params: {
                            search,
                        },
                    }
                );

                if (requestId !== this.searchRequestId) {
                    return;
                }

                this.countries = response.data.data ?? [];
            } catch (error) {
                if (requestId !== this.searchRequestId) {
                    return;
                }

                this.error = this.getErrorMessage(
                    error,
                    'Unable to search countries.'
                );
            } finally {
                if (requestId === this.searchRequestId) {
                    this.searching = false;
                }
            }
        },

        async addCountry(country) {
            if (this.savingCountryUuid) {
                return;
            }

            this.savingCountryUuid = country.uuid;
            this.error = null;

            try {
                await this.$http.post(
                    `/lite-integrations/${this.liteIntegration.uuid}/countries`,
                    {
                        country_uuid: country.uuid,
                    }
                );

                this.selectedCountries = [
                    ...this.selectedCountries,
                    country,
                ];

                this.countries = this.countries.filter(
                    ({ uuid }) => uuid !== country.uuid
                );
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to add country.'
                );
            } finally {
                this.savingCountryUuid = null;
            }
        },

        async removeCountry(country) {
            if (this.savingCountryUuid) {
                return;
            }

            this.savingCountryUuid = country.uuid;
            this.error = null;

            try {
                await this.$http.delete(
                    `/lite-integrations/${this.liteIntegration.uuid}` +
                    `/countries/${country.uuid}`
                );

                this.selectedCountries =
                    this.selectedCountries.filter(
                        ({ uuid }) => uuid !== country.uuid
                    );
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to remove country.'
                );
            } finally {
                this.savingCountryUuid = null;
            }
        },

        async updateCountriesIncluded() {
            if (this.savingMode) {
                return;
            }

            this.savingMode = true;
            this.error = null;

            try {
                const response = await this.$http.patch(
                    `/lite-integrations/${this.liteIntegration.uuid}/countries-mode`,
                    {
                        countries_included: this.countriesIncluded,
                    }
                );

                const updatedLiteIntegration =
                    response.data.data ??
                    response.data;

                this.countriesIncluded =
                    updatedLiteIntegration.countries_included === true ||
                    Number(
                        updatedLiteIntegration.countries_included
                    ) === 1;

                this.$emit(
                    'integration-updated',
                    updatedLiteIntegration
                );
            } catch (error) {
                this.countriesIncluded = !this.countriesIncluded;

                this.error = this.getErrorMessage(
                    error,
                    'Unable to update country inclusion mode.'
                );
            } finally {
                this.savingMode = false;
            }
        },

        getErrorMessage(error, fallback) {
            return error.response?.data?.message ?? fallback;
        },
    },
};
</script>